import { createNhostClient } from "@nhost/nhost-js";
import {
  sessionRefreshMiddleware,
  updateSessionFromResponseMiddleware,
  attachAccessTokenMiddleware,
} from "@nhost/nhost-js/fetch";

// Use createNhostClient (not createClient) with a custom middleware configuration.
//
// SDK Bug: The built-in withClientSideSessionMiddleware pushes sessionRefreshMiddleware
// onto the auth client. That middleware skips the token endpoint by checking
// url.endsWith('/v1/token'), but self-hosted Nhost uses '/token' (no /v1/ prefix).
// This causes a recursive Web Lock deadlock:
//   refreshSession() → exclusive lock → auth.refreshToken() → middleware → refreshSession()
//   → tries shared lock → BLOCKED by exclusive lock → deadlock
//
// Fix: Apply sessionRefreshMiddleware only to storage/graphql/functions (not auth).
// Auth client only needs token attachment and session update middleware.
export const nhost = createNhostClient({
  authUrl: process.env.NEXT_PUBLIC_NHOST_AUTH_URL || "http://localhost:1337",
  graphqlUrl:
    process.env.NEXT_PUBLIC_NHOST_GRAPHQL_URL ||
    "http://localhost:8080/v1/graphql",
  // Must end in /v1: the SDK builds every request as `${baseURL}/files`, and
  // hasura-storage serves its API under /v1 — a bare host 404s every upload.
  storageUrl:
    process.env.NEXT_PUBLIC_NHOST_STORAGE_URL || "http://localhost:1338/v1",
  configure: [
    ({ auth, storage, graphql, functions, sessionStorage }) => {
      // Session refresh middleware — only for non-auth clients to avoid recursive lock
      const refreshMw = sessionRefreshMiddleware(auth, sessionStorage);
      storage.pushChainFunction(refreshMw);
      graphql.pushChainFunction(refreshMw);
      functions.pushChainFunction(refreshMw);

      // Session update + token attachment — safe for all clients including auth
      const updateMw = updateSessionFromResponseMiddleware(sessionStorage);
      const tokenMw = attachAccessTokenMiddleware(sessionStorage);
      for (const mw of [updateMw, tokenMw]) {
        auth.pushChainFunction(mw);
        storage.pushChainFunction(mw);
        graphql.pushChainFunction(mw);
        functions.pushChainFunction(mw);
      }
    },
  ],
});

export const { auth, graphql, storage } = nhost;
